import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "lib/utils";
import { forwardRef, type InputHTMLAttributes } from "react";

const toggleVariants = cva("toggle", {
  variants: {
    size: {
      xs: "toggle-xs",
      sm: "toggle-sm",
      md: "toggle-md",
      lg: "toggle-lg",
    },
    color: {
      default: "",
      primary: "toggle-primary",
      secondary: "toggle-secondary",
      accent: "toggle-accent",
      info: "toggle-info",
      success: "toggle-success",
      warning: "toggle-warning",
      error: "toggle-error",
    },
  },
  defaultVariants: {
    size: "sm",
    color: "default",
  },
});

type ToggleVariantProps = VariantProps<typeof toggleVariants>;

export type ToggleProps = {
  label?: string;
  size?: ToggleVariantProps["size"];
  color?: ToggleVariantProps["color"];
} & Omit<InputHTMLAttributes<HTMLInputElement>, "className" | "size" | "type">;

/**
 * 純粋な Toggle リーフ。`<input type="checkbox">` を DaisyUI の toggle として表示する。
 * @param {ToggleProps} props Toggle の props
 * @return {JSX.Element} Toggle 要素
 */
export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  ({ label, size, color, ...rest }, ref) => {
    const input = (
      <input
        ref={ref}
        type="checkbox"
        className={cn(toggleVariants({ size, color }))}
        {...rest}
      />
    );

    if (!label) {
      return input;
    }

    return (
      <label className="label cursor-pointer justify-start gap-2">
        <span className="label-text">{label}</span>
        {input}
      </label>
    );
  },
);
Toggle.displayName = "Toggle";
